import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { fetch } from '@ngrx/router-store/data-persistence';
import { Store } from '@ngrx/store';
import {
  filter as _filter,
  first,
  isInteger,
  isNil,
  isUndefined,
  omit,
  pick,
  toInteger,
} from 'lodash';
import {
  catchError,
  combineLatest,
  filter,
  map,
  merge,
  of,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';

import { BrokerDetailsActions } from '@lockton-apps/brokers/action';
import { ClientsTransformationService } from '@lockton-apps/client-gateway/transformations';
import {
  ClientActions,
  ClientListActions,
  ClientNewActions,
  ClientSubClientsNewActions,
  ClientUpdateActions,
} from '@lockton-apps/clients/actions';
import { ContactCreateUpdateActions } from '@lockton-apps/contacts/action';
import {
  DashboardAdminActions,
  DashboardBrokerActions,
  DashboardBrokerLsosActions,
  DashboardBrokerNorwayActions,
  DashboardContactActions,
} from '@lockton-apps/dashboard/action';
import {
  NewEnquiryActions,
  UpdateEnquiryActions,
} from '@lockton-apps/enquiries/action';
import { LsosRenewalsOverviewActions } from '@lockton-apps/lsos-renewals/action';
import { PolicyCreateUpdateActions } from '@lockton-apps/policies/action';
import {
  NorwayProposalActions,
  ProposalShellActions,
} from '@lockton-apps/proposals/action';
import { QuoteDetailsActions } from '@lockton-apps/quotes/action';
import { AuthApiActions } from '@lockton-apps/shared/action/auth';
import { ClientApiActions } from '@lockton-apps/shared/action/clients';
import { ContactsApiActions } from '@lockton-apps/shared/action/contacts';
import { FormDialiogActions } from '@lockton-apps/shared/action/misc';
import { ProposalsApiActions } from '@lockton-apps/shared/action/proposals';
import { QuotesApiActions } from '@lockton-apps/shared/action/quotes';
import { AuthSelectors } from '@lockton-apps/shared/data-access/auth';
import {
  ClientRouterSelectors,
  selectQueryParam,
  selectRouteParam,
} from '@lockton-apps/shared/data-access/router';
import { Client } from '@lockton-apps/shared/interfaces';
import {
  FormDialogCloseResult,
  FormDialogComponent,
  FormDialogData,
} from '@lockton-apps/shared/ui/form-dialog';
import { FeedbackService } from '@lockton-apps/shared/ui/lui';

import { ClientsService } from '../services/clients.service';
import { ProposalDocumentBuilderActions } from '@lockton-apps/proposal-documents/action';

const MESSAGES = {
  addedSuccess: $localize`Client added successfully`,
  updatedSuccess: $localize`Client updated successfully`,
};

@Injectable()
export class ClientsEffects {
  private store = inject(Store);
  private actions$ = inject(Actions);
  private clientsService = inject(ClientsService);
  private feedbackService = inject(FeedbackService);
  private router = inject(Router);
  private clientTransformationService = inject(ClientsTransformationService);
  private dialog = inject(MatDialog);

  loadAllClients$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        DashboardAdminActions.init,
        DashboardBrokerLsosActions.init,
        DashboardBrokerNorwayActions.init,
        DashboardBrokerActions.init
      ),
      switchMap(() =>
        this.clientsService.all().pipe(
          map((clients) => ClientApiActions.loadClientsSuccess({ clients })),
          catchError((error) =>
            of(ClientApiActions.loadClientsFailure({ error }))
          )
        )
      )
    )
  );

  loadClient$ = createEffect(() =>
    merge(
      this.actions$.pipe(
        ofType(LsosRenewalsOverviewActions.init, ProposalShellActions.init),
        concatLatestFrom(() => this.store.select(selectRouteParam('clientId'))),
        map(([, clientId]) => clientId)
      ),
      this.actions$.pipe(
        ofType(PolicyCreateUpdateActions.init, NewEnquiryActions.init),
        concatLatestFrom(() =>
          this.store.select(ClientRouterSelectors.selectQueriedId)
        ),
        map(([, clientId]) => clientId)
      ),
      this.actions$.pipe(
        ofType(QuoteDetailsActions.init, UpdateEnquiryActions.init),
        switchMap(() =>
          this.actions$.pipe(
            ofType(
              QuotesApiActions.loadQuoteSuccess,
              QuotesApiActions.loadQuoteWithPropFormSuccess
            ),
            map(({ quote }) => quote.clientId)
          )
        )
      ),
      this.actions$.pipe(
        ofType(ProposalDocumentBuilderActions.init),
        switchMap(() =>
          this.actions$.pipe(
            ofType(ProposalsApiActions.loadProposalSuccess),
            map(({ proposal }) => proposal.clientId)
          )
        )
      )
    ).pipe(
      switchMap((clientId) => {
        if (!clientId) {
          throw new Error('No client id');
        }
        return this.clientsService.load(toInteger(clientId)).pipe(
          map((client) => ClientApiActions.loadClientSuccess({ client })),
          catchError((error) =>
            of(ClientApiActions.loadClientFailure({ error }))
          )
        );
      }),
      catchError((error) => of(ClientApiActions.loadClientFailure({ error })))
    )
  );

  addPrimaryClient$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClientNewActions.clientDataAdded),
      switchMap(({ client, contact, subClients }) =>
        this.clientsService.create(client).pipe(
          map((client) =>
            ClientApiActions.primaryClientAddedSuccess({
              client,
              contact,
              subClients,
            })
          ),
          catchError((error) =>
            of(ClientApiActions.primaryClientAddedFailure({ error }))
          )
        )
      )
    )
  );

  completeAddClientData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClientNewActions.clientDataAdded),
      switchMap(() =>
        combineLatest([
          this.actions$.pipe(
            ofType(ClientApiActions.primaryClientAddedSuccess)
          ),
          this.actions$.pipe(
            ofType(ContactsApiActions.addPrimaryClientContactSuccess)
          ),
          this.actions$.pipe(
            ofType(ClientApiActions.secondaryClientsAddedComplete)
          ),
        ]).pipe(
          map(([{ client }]) =>
            ClientApiActions.clientDataAddedComplete({ client })
          )
        )
      )
    )
  );

  addSubClient$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ClientApiActions.primaryClientAddedSuccess,
        ClientApiActions.secondaryClientAddedSuccess
      ),
      filter(({ subClients }) => subClients.length > 0),
      switchMap(({ client, subClients: [firstClient, ...tailClients] }) =>
        this.clientsService
          .create({ ...firstClient, relatesToClientId: client.id })
          .pipe(
            map((subClient) =>
              ClientApiActions.secondaryClientAddedSuccess({
                client,
                addedClient: subClient,
                subClients: tailClients,
              })
            )
          )
      )
    )
  );

  completeAddSubClients$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ClientApiActions.primaryClientAddedSuccess,
        ClientApiActions.secondaryClientAddedSuccess
      ),
      filter(({ subClients }) => subClients.length === 0),
      switchMap(() => of(ClientApiActions.secondaryClientsAddedComplete()))
    )
  );

  updateClient$ = createEffect(() =>
    merge(
      this.actions$.pipe(
        ofType(ClientUpdateActions.clientUpdated),
        concatLatestFrom(() => this.store.select(selectRouteParam('clientId'))),
        map(([{ clientModel }, clientId]) =>
          this.clientTransformationService.clientUpdate(
            clientModel,
            toInteger(clientId)
          )
        )
      ),
      this.actions$.pipe(
        ofType(
          FormDialiogActions.clientUpdated,
          FormDialiogActions.clientAndContactUpdated
        ),
        map(({ client }) => client)
      )
    ).pipe(
      switchMap((client) =>
        this.clientsService.update(client).pipe(
          map((client) => ClientApiActions.clientUpdatedSuccess({ client })),
          catchError((error) =>
            of(ClientApiActions.clientUpdatedFailure({ error }))
          )
        )
      ),
      catchError((error) =>
        of(ClientApiActions.clientUpdatedFailure({ error }))
      )
    )
  );

  loadClientAndSubClients$ = createEffect(() =>
    merge(
      this.actions$.pipe(ofType(ContactCreateUpdateActions.init)).pipe(
        concatLatestFrom(() =>
          this.store.select(ClientRouterSelectors.selectQueriedId)
        ),
        map(([, clientId]) => clientId),
        concatLatestFrom(() =>
          this.store.select(AuthSelectors.selectIsContact)
        ),
        filter(([, isContact]) => !isContact),
        map(([clientId]) => clientId)
      ),
      this.actions$.pipe(
        ofType(AuthApiActions.contactAuthenticated),
        switchMap(() =>
          this.actions$.pipe(
            ofType(ContactsApiActions.loadAuthenticatedContactSuccess),
            filter(
              ({ contact }) =>
                _filter(contact.clients, { isPrimary: true }).length > 0
            )
          )
        ),
        // TODO: handle all primary clients currently only handles first
        map(({ contact }) => {
          const primaryClient = first(
            _filter(contact.clients, { isPrimary: true })
          ) as { id: number; isPrimary: true };
          return primaryClient.id;
        })
      )
    ).pipe(
      switchMap((clientId) =>
        this.clientsService.loadClientAndSubClients(clientId as number).pipe(
          map((clients) =>
            ClientApiActions.loadClientAndSubClientsSuccess({ clients })
          ),
          catchError((error) =>
            of(ClientApiActions.loadClientAndSubClientFailure({ error }))
          )
        )
      )
    )
  );

  addClient$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClientSubClientsNewActions.clientAdded),
      withLatestFrom(
        this.store.select(ClientRouterSelectors.selectRoutedId),
        ({ type, client }, selectedClientId) => ({
          type,
          subClient: client,
          parentClientId: selectedClientId,
        })
      ),
      filter(({ parentClientId }) => isInteger(parentClientId)),
      fetch({
        run: ({ subClient }) =>
          this.clientsService.create(subClient).pipe(
            map((res) =>
              ClientApiActions.clientAddedSuccess({
                client: res,
              })
            )
          ),
        onError: (_, error) =>
          ClientApiActions.clientAddedFailure({
            error,
          }),
      })
    )
  );

  // New
  checkClientRelationshipsFromQueryParams$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ContactCreateUpdateActions.init),
      concatLatestFrom(() => this.store.select(selectQueryParam('client'))),
      filter(([, clientId]) => !isUndefined(clientId)),
      switchMap(([, clientId]) => {
        if (!clientId) {
          throw new Error('No client Id');
        }
        return this.clientsService.load(toInteger(clientId)).pipe(
          map((client) =>
            ClientApiActions.loadClientSuccessWithRelationshipChecks({
              client,
            })
          ),
          catchError((error) =>
            of(ClientApiActions.loadClientFailure({ error }))
          )
        );
      })
    )
  );

  checkClientRelationshipsFromRouteParams$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClientActions.init),
      concatLatestFrom(() => this.store.select(selectRouteParam('clientId'))),
      switchMap(([, clientId]) => {
        if (!clientId) {
          throw new Error('No client Id');
        }
        return this.clientsService.load(toInteger(clientId)).pipe(
          map((client) =>
            ClientApiActions.loadClientSuccessWithRelationshipChecks({
              client,
            })
          ),
          catchError((error) =>
            of(ClientApiActions.loadClientFailure({ error }))
          )
        );
      })
    )
  );

  checkClientRelationshipsFromAction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ContactsApiActions.loadContactSuccessThenLoadClient),
      switchMap(({ clientId }) => {
        if (!clientId) {
          throw new Error('No client Id');
        }
        return this.clientsService.load(toInteger(clientId)).pipe(
          map((client) =>
            ClientApiActions.loadClientSuccessWithRelationshipChecks({
              client,
            })
          ),
          catchError((error) =>
            of(ClientApiActions.loadClientFailure({ error }))
          )
        );
      })
    )
  );

  loadClientSubClientsNew$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ClientApiActions.loadClientSuccessWithRelationshipChecks,
        ClientApiActions.loadPrimaryClientSuccess
      ),
      filter(({ client }) => client.isPrimaryClient),
      switchMap(({ client }) =>
        this.clientsService.loadClientSubClients(client.id).pipe(
          map((clients) =>
            ClientApiActions.loadClientSubClientSuccess({ clients })
          ),
          catchError((error) =>
            of(ClientApiActions.loadClientSubClientFailure({ error }))
          )
        )
      )
    )
  );

  checkRelatesToAndLoadPrimaryClient$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClientApiActions.loadClientSuccessWithRelationshipChecks),
      filter(({ client }) => !isNil(client.relatesToClientId)),
      switchMap(({ client }) =>
        this.clientsService.load(client.relatesToClientId as number).pipe(
          map((client) =>
            ClientApiActions.loadPrimaryClientSuccess({ client })
          ),
          catchError((error) =>
            of(ClientApiActions.loadClientFailure({ error }))
          )
        )
      )
    )
  );

  loadClientsForContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthApiActions.contactAuthenticated, DashboardContactActions.init),
      switchMap(() =>
        this.clientsService.allForContact().pipe(
          map((clients) =>
            ClientApiActions.loadClientsSuccess({
              clients: clients || [],
            })
          ),
          catchError((error) =>
            of(ClientApiActions.loadClientsFailure({ error }))
          )
        )
      )
    )
  );

  // Messages

  showAddClientSuccessMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ClientApiActions.clientAddedSuccess),
        tap(() => {
          this.feedbackService.subtleMessage(MESSAGES.addedSuccess, {
            type: 'success',
          });
        })
      ),
    { dispatch: false }
  );

  updateClientSuccessMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ClientApiActions.clientUpdatedSuccess),
        tap(() => {
          this.feedbackService.subtleMessage(MESSAGES.updatedSuccess, {
            type: 'success',
          });
        })
      ),
    { dispatch: false }
  );

  // Navigation / redirects

  navigateToClientHome$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ClientApiActions.clientAddedSuccess),
        tap(({ client }) => {
          this.router.navigate(['/clients', client.id]);
        })
      ),
    { dispatch: false }
  );

  navigateToClientDetails$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ClientUpdateActions.clientUpdated),
        switchMap(() =>
          this.actions$.pipe(
            ofType(ClientApiActions.clientUpdatedSuccess),
            tap(({ client }) => {
              this.router.navigate(['/clients', client.id, 'details']);
            })
          )
        )
      ),
    { dispatch: false }
  );

  openEditClientDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NorwayProposalActions.editClientClicked),
      switchMap(({ client }) => {
        // TODO: This logic is ideally in another service but used here. TBD
        return this.dialog
          .open<FormDialogComponent, FormDialogData, FormDialogCloseResult>(
            FormDialogComponent,
            {
              data: {
                title: $localize`Edit company details`,
                mode: 'edit',
                fields: [
                  {
                    fieldGroupClassName: 'vstack gap-1',
                    fieldGroup: [
                      {
                        key: 'clientData.insuredName',
                        type: 'input',
                        props: {
                          label: $localize`Company name`,
                          required: true,
                        },
                      },
                      {
                        key: 'customRef2',
                        type: 'input',
                        props: {
                          label: $localize`Organisation number`,
                          required: true,
                        },
                      },
                      {
                        key: 'address',
                        type: 'address',
                        props: {
                          label: $localize`Address`,
                          required: true,
                        },
                      },
                    ],
                  },
                ],
                model: {
                  ...client,
                  address: pick(client.clientData, [
                    'street',
                    'cityTown',
                    'country',
                    'postcode',
                  ]),
                },
                cancelLabel: $localize`Cancel`,
                confirmLabel: $localize`Save changes`,
              },
            }
          )
          .afterClosed()
          .pipe(
            filter((res) => !!res),
            map((res) => {
              if (!res) {
                throw new Error('No Client');
              }
              const updatedClient = {
                ...(omit(res.model, 'address') as Client),
                clientData: { ...res.model.clientData, ...res.model.address },
              };
              return FormDialiogActions.clientUpdated({
                client: updatedClient,
              });
            })
          );
      })
    )
  );
}
